import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import CanonicalTag from "@/components/CanonicalTag";
import SWUpdateToast from "@/components/SWUpdateToast";

const Index = lazy(() => import("./pages/Index"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
import UpdatePassword from "./pages/UpdatePassword";
const AdminPage = lazy(() => import("./pages/AdminPage"));
const UpgradePage = lazy(() => import("./pages/UpgradePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const FlashcardsPage = lazy(() => import("./pages/FlashcardsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const QuizzesPage = lazy(() => import("./pages/QuizzesPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CommunityPostPage = lazy(() => import("./pages/CommunityPostPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const AddFriendPage = lazy(() => import("./pages/AddFriendPage"));
const NieuwsPage = lazy(() => import("./pages/NieuwsPage"));
const VriendenPage = lazy(() => import("./pages/VriendenPage"));

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading...</div>
);

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <CanonicalTag />
    <Routes>
      <Route path="/auth" element={<AuthRoute><Auth /></AuthRoute>} />
      <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/reset-password" element={<Navigate to="/update-password" replace />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
      <Route path="/flashcards" element={<ProtectedRoute><FlashcardsPage /></ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
      <Route path="/quizzes" element={<ProtectedRoute><QuizzesPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
      <Route path="/nieuws" element={<ProtectedRoute><NieuwsPage /></ProtectedRoute>} />
      <Route path="/vrienden" element={<ProtectedRoute><VriendenPage /></ProtectedRoute>} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/community/:slug" element={<CommunityPostPage />} />
      <Route path="/profile/:username" element={<ProfilePage />} />
      <Route path="/invite/:code" element={<InvitePage />} />
      <Route path="/add-friend/:userId" element={<AddFriendPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <SWUpdateToast />
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
